import {
  DunningBillGroupResponse,
  DunningPauseResumeReq,
  GetAllDunningProcessResponse,
  IDunnings,
} from '@monetize/types/app';
import {
  ApiListResponse,
  GenericApiResponse,
  GetListApiFilter,
  TDataTablePager,
} from '@monetize/types/common';
import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import { useToast } from '../services/toast';
import { apiGet, apiGetAllList, apiPut } from './axios';
import { composeFiltersQuery } from './utils';

export function usePauseBillGroupDunning(
  options: Partial<
    UseMutationOptions<GenericApiResponse, unknown, DunningPauseResumeReq>
  > = {},
) {
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: async ({ billGroupId, pauseData }) => {
      const response = await apiPut<GenericApiResponse>(
        `/api/billGroups/${billGroupId}/pauseDunning`,
        pauseData,
      );
      return response.data;
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: ['billGroup', variables.billGroupId],
      });

      addToast({
        severity: 'success',
        summary: 'Success',
        detail: 'Dunning process has been paused successfully',
      });

      onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
}

export function useResumeBillGroupDunning(
  options: Partial<
    UseMutationOptions<
      GenericApiResponse,
      unknown,
      Pick<DunningPauseResumeReq, 'billGroupId'>
    >
  > = {},
) {
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: async ({ billGroupId }) => {
      const response = await apiPut<GenericApiResponse>(
        `/api/billGroups/${billGroupId}/restartDunning`,
      );
      return response.data;
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: ['billGroup', variables.billGroupId],
      });

      addToast({
        severity: 'success',
        summary: 'Success',
        detail: 'Dunning process has been resumed successfully',
      });

      onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
}

export function useGetDunningBillGroups({
  dunningProcessId,
  pager,
  filters = {},
  options = {},
}: {
  dunningProcessId?: string;
  pager: TDataTablePager;
  filters?: GetListApiFilter;
  options?: Partial<UseQueryOptions<ApiListResponse<DunningBillGroupResponse>>>;
}) {
  return useQuery({
    queryKey: [
      'dunningBillGroups',
      dunningProcessId,
      pager.page,
      pager.rows,
      filters,
    ],
    enabled: !!dunningProcessId,
    queryFn: async ({ signal }) => {
      const response = await apiGet<ApiListResponse<DunningBillGroupResponse>>(
        `/api/dunning/${dunningProcessId}/billGroups`,
        {
          params: {
            currentPage: pager.page,
            pageSize: pager.rows,
            ...composeFiltersQuery(filters),
          },
          signal,
        },
      );

      return response.data;
    },
    ...options,
  });
}

export function useGetAllDunningProcesses(
  options: Partial<
    UseQueryOptions<IDunnings[], unknown, GetAllDunningProcessResponse>
  > = {},
) {
  return useQuery({
    queryKey: ['allDunningProcesses'],
    queryFn: async (): Promise<IDunnings[]> => {
      return await apiGetAllList<IDunnings>('/api/dunning/dunningProcess');
    },
    select: (processes: IDunnings[]): GetAllDunningProcessResponse => ({
      dunningProcesses: processes,
      dunningProcessOptions: processes.map((dunningProcess) => ({
        label: dunningProcess.name,
        value: dunningProcess.id,
      })),
    }),
    ...options,
  });
}
